import { useMemo } from 'react';
import {
  Address,
  Project,
  ProjectStages,
  useAddress,
  useDeleteProject,
  useGroupRules,
  UserGroupRules,
} from 'src/graphql';
import { EntityNames } from 'src/graphql/types';
import { Modals, useShowModals } from 'src/store/modals';
import { useConfirmationModal, useCopyToClipboard, useEntityFullUrl } from 'src/utils/hooks';

export const useProjectActions = ({
  project,
  address,
}: {
  project: Project;
  address: Address;
}) => {
  const { confirm } = useConfirmationModal();
  const { copyToClipboard } = useCopyToClipboard();
  const { deleteProject } = useDeleteProject();
  const { updateAddress } = useAddress(address.addressId);
  const [openSubmitDay1Modal, openProposalWonModal, openProposalLostModal] = useShowModals(
    Modals.SubmitDay1,
    Modals.ProposalWon,
    Modals.ProposalLost,
  );

  const { groupRules } = useGroupRules();

  const enabledChangeProjectStatus = Boolean(
    groupRules[UserGroupRules.AccessToChangeProjectStatus],
  );

  const projectFullUrl = useEntityFullUrl(project.partnerId, EntityNames.Project, project.id);

  return useMemo(
    () => ({
      submitDay1: {
        label: 'Submit Day1',
        icon: 'today',
        isAllowed:
          project.projectStageId === ProjectStages.ActiveProject && enabledChangeProjectStatus,
        handler: () => openSubmitDay1Modal({ address, project }),
      },
      proposalWon: {
        label: 'Proposal won',
        icon: 'check_circle',
        isAllowed:
          project.projectStageId === ProjectStages.Proposal && enabledChangeProjectStatus,
        handler: () => openProposalWonModal({ address, project }),
      },
      proposalLost: {
        label: 'Proposal lost',
        icon: 'highlight_off',
        isAllowed:
          ![ProjectStages.ServiceClient, ProjectStages.ProposalLost].includes(
            project.projectStageId,
          ) && enabledChangeProjectStatus,
        handler: () => openProposalLostModal({ address, project }),
      },
      copyProjectId: {
        label: 'Copy Project ID',
        icon: 'content_copy',
        handler: async () => {
          await copyToClipboard(project.id);
        },
      },
      copyProjectUrl: {
        label: 'Copy Project URL',
        icon: 'link',
        isAllowed: !!projectFullUrl,
        handler: async () => {
          await copyToClipboard(projectFullUrl);
        },
      },
      deleteProject: {
        label: 'Delete',
        icon: 'delete',
        handler: async () => {
          const confirmed = await confirm({
            body: 'Please confirm you want \nto delete project from the site.',
            variant: 'delete',
          });
          if (confirmed) {
            if (address.primaryProjectId === project.id) {
              await updateAddress({
                ...address,
                primaryProjectId: null,
              });
            }
            const { errors } = await deleteProject(project.id);
            if (errors) {
              console.error(errors);
              return;
            }
          }
        },
      },
    }),
    [
      address,
      confirm,
      copyToClipboard,
      deleteProject,
      enabledChangeProjectStatus,
      openProposalLostModal,
      openProposalWonModal,
      openSubmitDay1Modal,
      project,
      projectFullUrl,
      updateAddress,
    ],
  );
};
